import React from 'react';
import { Dialog, Box, Typography } from '@mui/material';
import { associationModalStyles } from './AssociationModal.styles';
import { useTranslation } from "react-i18next";

interface AssociationModalProps {
  open: boolean;
  panNumber?: string;
  currentCustomerId?: string;
  currentCustomerShopName?: string;
  associatedCustomerDataList: Record<string, unknown>[];
  bankDetails?: {
    bankName?: string;
    bankAccountNumber?: string;
    ifscCode?: string;
  };
  onClose: () => void;
}

const getStringValue = (item: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = item[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number') {
      return String(value);
    }
  }
  return '';
};

export function AssociationModal({
  open,
  panNumber,
  currentCustomerId,
  currentCustomerShopName,
  associatedCustomerDataList,
  bankDetails,
  onClose,
}: AssociationModalProps) {
  const { t } = useTranslation();

  const sortedList = React.useMemo(() => {
    if (!currentCustomerId) return associatedCustomerDataList;
    const currentItem = associatedCustomerDataList.find((item) => {
      const id = getStringValue(item, ['customerId', 'customerID', 'linkedCustomerId', 'customerid']);
      return id === currentCustomerId;
    });
    if (currentItem) {
      const rest = associatedCustomerDataList.filter((item) => item !== currentItem);
      return [currentItem, ...rest];
    }
    const currentUserEntry: Record<string, unknown> = { customerId: currentCustomerId, shopName: currentCustomerShopName || '' };
    return [currentUserEntry, ...associatedCustomerDataList];
  }, [associatedCustomerDataList, currentCustomerId, currentCustomerShopName]);

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: associationModalStyles.paper }}>
      <Box sx={associationModalStyles.container}>
        {/* Header */}
        <Box sx={associationModalStyles.headerRow}>
          <Typography sx={associationModalStyles.title}>
            {t('bank_details_auto_filled_title', { defaultValue: 'Bank Details Auto Filled' })}
          </Typography>
          <Box onClick={onClose} sx={associationModalStyles.closeButton}>
            <svg fill="none" viewBox="0 0 14 14" width="14" height="14">
              <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="black" fillOpacity="0.56" />
            </svg>
          </Box>
        </Box>

        {/* PAN linked firms section */}
        <Box sx={associationModalStyles.linkedSection}>
          <Typography sx={associationModalStyles.linkedText}>
            {t('pan_label', { defaultValue: 'PAN' })}{' '}
            <Typography component="span" sx={associationModalStyles.panNumberBold}>
              {panNumber}
            </Typography>
            {' '}{t('is_linked_to_below_firms', { defaultValue: 'is linked to below firms:' })}
          </Typography>
          <Box component="ul" sx={associationModalStyles.firmsList}>
            {sortedList.map((item, index) => {
              const customerId = getStringValue(item, ['customerId', 'customerID', 'linkedCustomerId', 'customerid']);
              const customerName = getStringValue(item, ['shopName', 'firmName', 'customerName', 'name']);
              return (
                <Box component="li" key={`${customerId || 'customer'}-${index}`} sx={associationModalStyles.firmsListItem}>
                  <Typography sx={associationModalStyles.firmsListText}>
                    {`${t('customer_id', { defaultValue: 'Customer ID' })}: ${customerId}${customerName ? ` (${customerName})` : ''}`}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Bank details card section */}
        <Box sx={associationModalStyles.bankSection}>
          <Typography sx={associationModalStyles.bankSectionMessage}>
            {t('same_bank_payout_message', { defaultValue: "The same bank account will be used for this firm's payouts." })}
          </Typography>
          <Box sx={associationModalStyles.bankCard}>
            <Box sx={associationModalStyles.bankCardRow}>
              <Typography sx={associationModalStyles.bankCardLabel}>
                {t('bank_name', { defaultValue: 'Bank Name' })}
              </Typography>
              <Typography sx={associationModalStyles.bankCardValue}>
                {bankDetails?.bankName || '-'}
              </Typography>
            </Box>
            <Box sx={associationModalStyles.bankCardRow}>
              <Typography sx={associationModalStyles.bankCardLabel}>
                {t('account_no', { defaultValue: 'Account No.' })}
              </Typography>
              <Typography sx={associationModalStyles.bankCardValue}>
                {bankDetails?.bankAccountNumber || '-'}
              </Typography>
            </Box>
            <Box sx={associationModalStyles.bankCardRow}>
              <Typography sx={associationModalStyles.bankCardLabel}>
                {t('ifsc_code', { defaultValue: 'IFSC Code' })}
              </Typography>
              <Typography sx={associationModalStyles.bankCardValue}>
                {bankDetails?.ifscCode || '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
